import React, { useState, useEffect, useCallback } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonText,
  IonSpinner,
  useIonAlert,
  IonList,
  IonListHeader,
  IonCheckbox
} from '@ionic/react';
import { barcodeOutline, searchOutline, swapHorizontalOutline, closeOutline, addOutline, removeOutline } from 'ionicons/icons';
import getApi from '../api';
import { useLocalStorage } from '../components/useLocalStorage';
import { useScannerBridge } from '../hooks/useScannerBridge';
import './WarehouseTransfer.css';

interface StockLocation {
  id: string;
  code: string;
  name?: string;
}

interface StockPoint {
  id: string;
  code: string;
  name: string;
}

interface TransferItem {
  articleNumber: string;
  quantity: number;
  fromStockPointId: string;
  fromStockPointCode: string;
  fromStockLocationId: string | null;
  fromStockLocationCode: string | null;
  toStockPointId: string;
  toStockPointCode: string;
  toStockLocationId: string | null;
  toStockLocationCode: string | null;
}

interface StockLocationDetail {
  stockPointId: string;
  stockLocationId: string | null;
  stockLocationCode: string | null;
  stockLocationName: string | null;
  totalQuantity: number;
  availableQuantity: number;
}

interface StockSummary {
  stockPointId: string;
  stockPointCode: string;
  stockPointName: string;
  totalQuantity: number;
  availableQuantity: number;
  orderShortage: number;
  stockLocations: number;
  orderPointQuantity: number | null;
  remainingOrderedQuantity: number;
  maxStock: number | null;
  stockLocationDetails: StockLocationDetail[];
}

const WarehouseTransfer: React.FC = () => {
  const [presentAlert] = useIonAlert();
  const [me] = useLocalStorage("me", "");
  const api = getApi();

  const [articleNumber, setArticleNumber] = useState('');
  const [exactArticleNumber, setExactArticleNumber] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stockSummary, setStockSummary] = useState<StockSummary[]>([]);
  const [stockPoints, setStockPoints] = useState<StockPoint[]>([]);
  const [availableStockPoints, setAvailableStockPoints] = useState<StockPoint[]>([]);
  const [fromStockPoint, setFromStockPoint] = useState('');
  const [fromStockLocation, setFromStockLocation] = useState('');
  const [fromLocations, setFromLocations] = useState<StockLocation[]>([]);
  const [availableFromLocations, setAvailableFromLocations] = useState<StockLocation[]>([]);
  const [toStockPoint, setToStockPoint] = useState('');
  const [toStockLocation, setToStockLocation] = useState('');
  const [toLocations, setToLocations] = useState<StockLocation[]>([]);
  const [toStockLocationSearch, setToStockLocationSearch] = useState('');
  const [showToStockLocationSuggestions, setShowToStockLocationSuggestions] = useState(false);
  const [filteredToStockLocations, setFilteredToStockLocations] = useState<StockLocation[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [transferItems, setTransferItems] = useState<TransferItem[]>([]);
  const [updateStandardLocation, setUpdateStandardLocation] = useState<boolean>(false);
  const justSelectedLocation = React.useRef(false);

  useEffect(() => {
    loadStockPoints();
  }, []);

  const handleScan = useCallback((code: string) => {
    if (!code || scannerLoading) return;
    
    // Close modal but keep camera running for next scan
    setScannerActive(false);
    setScannerLoading(false);
    
    // Set article number and search
    setArticleNumber(code);
    searchArticle(code);
  }, [scannerLoading]);

  const { startScanner, stopScanner } = useScannerBridge({ autoStart: false, onScan: handleScan });

  const handleActivateScanner = async () => {
    if (scannerLoading || scannerActive) return;
    
    // Show modal immediately for instant feedback
    setScannerActive(true);
    setScannerLoading(true);
    
    // Start scanner in background
    try {
      await startScanner();
      setScannerLoading(false);
    } catch (error) {
      console.error('Failed to start scanner:', error);
      setScannerActive(false);
      setScannerLoading(false);
      presentAlert({
        header: 'Fel',
        message: 'Kunde inte starta skannern. Kontrollera behörigheter.',
        buttons: ['OK']
      });
    }
  };

  const handleStopScanner = () => {
    stopScanner();
    setScannerActive(false);
    setScannerLoading(false);
  };

  const loadStockPoints = async () => {
    try {
      const response = await api.get('/fortnox/warehouse/stockpoints/');
      if (response.data && Array.isArray(response.data)) {
        setStockPoints(response.data);
      }
    } catch (error) {
      console.error('Failed to load stock points:', error);
      presentAlert({
        header: 'Fel',
        message: 'Kunde inte ladda lagerplatser',
        buttons: ['OK']
      });
    }
  };

  const loadStockLocations = async (stockPointId: string, isFrom: boolean, currentStockSummary?: StockSummary[]) => {
    try {
      const response = await api.get(`/fortnox/warehouse/stockpoints/${stockPointId}/stocklocations/`);
      if (response.data && Array.isArray(response.data)) {
        if (isFrom) {
          setFromLocations(response.data);
          
          // Bygg lista från stockLocationDetails istället för att filtrera API-svar
          // Använd parameter om den finns, annars state
          const summaryToUse = currentStockSummary || stockSummary;
          
          console.log('loadStockLocations - stockPointId:', stockPointId);
          console.log('loadStockLocations - stockPoints:', stockPoints);
          console.log('loadStockLocations - summaryToUse:', summaryToUse);
          
          const stockPoint = stockPoints.find(sp => sp.id === stockPointId);
          console.log('loadStockLocations - found stockPoint:', stockPoint);
          
          if (stockPoint) {
            const stockForPoint = summaryToUse.find(s => s.stockPointCode === stockPoint.code);
            console.log('loadStockLocations - found stockForPoint:', stockForPoint);
            
            if (stockForPoint && stockForPoint.stockLocationDetails) {
              const locationsWithStock: StockLocation[] = [];
              
              console.log('loadStockLocations - stockLocationDetails:', stockForPoint.stockLocationDetails);
              
              // Lägg till alla locations med lager från stockLocationDetails
              stockForPoint.stockLocationDetails.forEach(detail => {
                if (detail.availableQuantity > 0) {
                  if (detail.stockLocationCode) {
                    // Hitta location info från API om det finns
                    const apiLocation = response.data.find((loc: StockLocation) => loc.code === detail.stockLocationCode);
                    locationsWithStock.push({
                      id: detail.stockLocationId || '',
                      code: detail.stockLocationCode,
                      name: apiLocation?.name || detail.stockLocationName || ''
                    });
                  } else {
                    // Lager utan specifik plats
                    locationsWithStock.push({
                      id: '',
                      code: '',
                      name: 'Ingen specifik plats'
                    });
                  }
                }
              });
              
              console.log('Available from locations:', locationsWithStock);
              setAvailableFromLocations(locationsWithStock);
            }
          }
        } else {
          setToLocations(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to load stock locations:', error);
      presentAlert({
        header: 'Fel',
        message: 'Kunde inte ladda lagerlokaler',
        buttons: ['OK']
      });
    }
  };

  const handleToStockLocationSearch = (value: string) => {
    setToStockLocationSearch(value || '');
    
    // Om vi precis valt något, visa inte listan
    if (justSelectedLocation.current) {
      return;
    }
    
    if (!value || !value.trim()) {
      setFilteredToStockLocations([]);
      setShowToStockLocationSuggestions(false);
      return;
    }
    
    const filtered = toLocations.filter(loc => 
      loc.code.toLowerCase().startsWith(value.toLowerCase()) ||
      (loc.name && loc.name.toLowerCase().startsWith(value.toLowerCase()))
    );
    setFilteredToStockLocations(filtered);
    setShowToStockLocationSuggestions(true);
  };

  const handleSelectToStockLocation = (location: StockLocation) => {
    justSelectedLocation.current = true;
    setToStockLocation(location.code);
    const displayText = `${location.code}${location.name ? ' - ' + location.name : ''}`;
    setToStockLocationSearch(displayText);
    setShowToStockLocationSuggestions(false);
    setFilteredToStockLocations([]);
    // Återställ flaggan efter en liten stund
    setTimeout(() => {
      justSelectedLocation.current = false;
    }, 300);
  };

  const handleStockPointChange = (value: string, isFrom: boolean) => {
    if (isFrom) {
      setFromStockPoint(value);
      setFromStockLocation('');
      setFromLocations([]);
      setAvailableFromLocations([]);
      
      // Sätt kvantitet till tillgängligt antal för stockpoint
      const stockForPoint = stockSummary.find(s => s.stockPointCode === value);
      if (stockForPoint && stockForPoint.availableQuantity > 0) {
        setQuantity(stockForPoint.availableQuantity);
      }
      
      // Hitta StockPoint ID för att ladda locations
      const stockPoint = stockPoints.find(sp => sp.code === value);
      if (stockPoint && stockPoint.id) {
        loadStockLocations(stockPoint.id, true);
      }
    } else {
      setToStockPoint(value);
      setToStockLocation('');
      setToLocations([]);
      
      const stockPoint = stockPoints.find(sp => sp.code === value);
      if (stockPoint && stockPoint.id) {
        loadStockLocations(stockPoint.id, false);
      }
    }
  };

  const handleFromLocationChange = (value: string) => {
    setFromStockLocation(value);
    
    // Sätt kvantitet till tillgängligt antal för vald location
    if (value && fromStockPoint) {
      const stockForPoint = stockSummary.find(s => s.stockPointCode === fromStockPoint);
      if (stockForPoint && stockForPoint.stockLocationDetails) {
        const detail = stockForPoint.stockLocationDetails.find(
          d => d.stockLocationCode === value
        );
        if (detail && detail.availableQuantity > 0) {
          setQuantity(detail.availableQuantity);
        }
      }
    }
  };

  const getMaxAvailableQuantity = (): number => {
    if (!fromStockPoint) return 1;
    
    const stockForPoint = stockSummary.find(s => s.stockPointCode === fromStockPoint);
    if (!stockForPoint) return 1;
    
    // Om en location är vald, använd dess antal
    if (fromStockLocation) {
      const detail = stockForPoint.stockLocationDetails.find(
        d => d.stockLocationCode === fromStockLocation
      );
      return detail?.availableQuantity || 1;
    }
    
    // Annars använd totala antalet för stockpoint
    return stockForPoint.availableQuantity || 1;
  };

  const handleIncreaseQuantity = () => {
    const maxQty = getMaxAvailableQuantity();
    if (quantity < maxQty) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleQuantityChange = (value: string) => {
    const newQty = parseInt(value, 10);
    if (isNaN(newQty) || newQty < 1) {
      setQuantity(1);
    } else {
      const maxQty = getMaxAvailableQuantity();
      setQuantity(Math.min(newQty, maxQty));
    }
  };

  const searchArticle = async (itemId: string) => {
    if (!itemId) return;

    setIsLoading(true);
    try {
      // Först, kolla om artikeln finns i systemet och hämta exakt accounting_connector
      let exactArticleNumber = itemId;
      try {
        const articleCheck = await api.get(`/bbe/articles/?accounting_connector=${itemId}`);
        const results = articleCheck.data?.results || articleCheck.data;
        
        if (results && Array.isArray(results) && results.length > 0) {
          // Använd det exakta accounting_connector från BBE (Fortnox är case-sensitive)
          exactArticleNumber = results[0].accounting_connector;
          setExactArticleNumber(exactArticleNumber);
          console.log(`Using exact article number from BBE: ${exactArticleNumber} (searched for: ${itemId})`);
        } else if (articleCheck.data?.count === 0 || (Array.isArray(results) && results.length === 0)) {
          // Inga resultat betyder att artikeln inte finns
          presentAlert({
            header: 'Artikel finns inte',
            message: `Artikelnummer ${itemId} finns inte i systemet`,
            buttons: ['OK']
          });
          setStockSummary([]);
          setAvailableStockPoints([]);
          setIsLoading(false);
          return;
        }
      } catch (checkError: any) {
        // Om vi får 404 betyder det att artikeln inte finns
        if (checkError.response?.status === 404) {
          presentAlert({
            header: 'Artikel finns inte',
            message: `Artikelnummer ${itemId} finns inte i systemet`,
            buttons: ['OK']
          });
          setStockSummary([]);
          setAvailableStockPoints([]);
          setIsLoading(false);
          return;
        }
        // För andra fel, fortsätt ändå (kanske API:et inte stöder denna endpoint)
        console.warn('Could not verify article existence:', checkError);
        exactArticleNumber = itemId; // Fallback to original input
      }

      // Om artikeln finns, hämta lagerstatus med exakt artikelnummer från BBE
      const response = await api.get(`/fortnox/itemstocksummary/${exactArticleNumber}/`);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        setStockSummary(response.data);
        
        // Filtrera stockPoints som har lager för denna artikel
        const stockPointsWithStock = response.data
          .filter((stock: StockSummary) => stock.availableQuantity > 0)
          .map((stock: StockSummary) => {
            return stockPoints.find(sp => sp.code === stock.stockPointCode);
          })
          .filter((sp): sp is StockPoint => sp !== undefined);
        
        setAvailableStockPoints(stockPointsWithStock);
        
        // Återställ valda värden
        setFromStockPoint('');
        setFromStockLocation('');
        setFromLocations([]);
        setAvailableFromLocations([]);
        
        // Om endast en stockpoint har lager, välj den automatiskt
        if (stockPointsWithStock.length === 1) {
          const firstStock = response.data[0];
          setFromStockPoint(firstStock.stockPointCode);
          
          // Sätt kvantitet till tillgängligt antal
          if (firstStock.availableQuantity > 0) {
            setQuantity(firstStock.availableQuantity);
          }
          
          // Ladda locations för första stockpoint
          const stockPoint = stockPoints.find(sp => sp.code === firstStock.stockPointCode);
          if (stockPoint && stockPoint.id) {
            loadStockLocations(stockPoint.id, true, response.data);
          }
        }
      } else {
        presentAlert({
          header: 'Ingen artikel hittades',
          message: `Artikelnummer ${itemId} hittades inte i lagret`,
          buttons: ['OK']
        });
        setStockSummary([]);
        setAvailableStockPoints([]);
      }
    } catch (error) {
      console.error('Failed to search article:', error);
      presentAlert({
        header: 'Fel',
        message: 'Kunde inte hämta artikelinformation',
        buttons: ['OK']
      });
      setStockSummary([]);
      setAvailableStockPoints([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualSearch = () => {
    if (articleNumber.trim()) {
      searchArticle(articleNumber.trim());
    }
  };

  const handleAddToList = () => {
    if (!articleNumber || !fromStockPoint || !toStockPoint) {
      presentAlert({
        header: 'Ofullständig information',
        message: 'Fyll i alla fält för att lägga till artikel',
        buttons: ['OK']
      });
      return;
    }

    // Validera att lagerplats har valts från listan (inte bara skrivits)
    if (toStockLocationSearch && !toStockLocation) {
      presentAlert({
        header: 'Välj lagerplats',
        message: 'Du måste välja en lagerplats från listan, inte bara skriva i fältet',
        buttons: ['OK']
      });
      return;
    }

    if (quantity <= 0) {
      presentAlert({
        header: 'Felaktig kvantitet',
        message: 'Antal måste vara större än 0',
        buttons: ['OK']
      });
      return;
    }

    // Hitta UUID:er för stockpoints och stocklocations
    const fromStockPointObj = stockPoints.find(sp => sp.code === fromStockPoint);
    const toStockPointObj = stockPoints.find(sp => sp.code === toStockPoint);
    const fromLocationObj = fromStockLocation ? fromLocations.find(loc => loc.code === fromStockLocation) : null;
    const toLocationObj = toStockLocation ? toLocations.find(loc => loc.code === toStockLocation) : null;

    if (!fromStockPointObj?.id || !toStockPointObj?.id) {
      presentAlert({
        header: 'Fel',
        message: 'Kunde inte hitta ID för lagerplatser',
        buttons: ['OK']
      });
      return;
    }

    const newItem: TransferItem = {
      articleNumber: exactArticleNumber || articleNumber,
      quantity,
      fromStockPointId: fromStockPointObj.id,
      fromStockPointCode: fromStockPoint,
      fromStockLocationId: fromLocationObj?.id || null,
      fromStockLocationCode: fromStockLocation || null,
      toStockPointId: toStockPointObj.id,
      toStockPointCode: toStockPoint,
      toStockLocationId: toLocationObj?.id || null,
      toStockLocationCode: toStockLocation || null
    };

    setTransferItems([...transferItems, newItem]);
    
    // Återställ formuläret men behåll lagerplatser
    setArticleNumber('');
    setExactArticleNumber('');
    setStockSummary([]);
    setQuantity(1);
  };

  const handleRemoveItem = (index: number) => {
    setTransferItems(transferItems.filter((_, i) => i !== index));
  };

  const handleTransfer = async () => {
    if (transferItems.length === 0) {
      presentAlert({
        header: 'Ingen artikel tillagd',
        message: 'Lägg till minst en artikel i listan innan du genomför flytten',
        buttons: ['OK']
      });
      return;
    }

    const firstItem = transferItems[0];
    const itemCount = transferItems.length;
    const totalQuantity = transferItems.reduce((sum, item) => sum + item.quantity, 0);

    presentAlert({
      header: 'Bekräfta flytt',
      message: `Flytta ${itemCount} artikel(ar) (totalt ${totalQuantity} st) från ${firstItem.fromStockPointCode} till ${firstItem.toStockPointCode}?`,
      buttons: [
        {
          text: 'Avbryt',
          role: 'cancel'
        },
        {
          text: 'Bekräfta',
          handler: async () => {
            setIsLoading(true);
            try {
              const transferData = {
                transferDate: new Date().toISOString().split('T')[0],
                note: `Lagerflytt av ${itemCount} artikel(ar)`,
                rows: transferItems.map(item => ({
                  itemId: item.articleNumber,
                  requestedQuantity: item.quantity,
                  fromStockLocationId: item.fromStockLocationId,
                  fromStockPointId: item.fromStockPointId,
                  toStockLocationId: item.toStockLocationId,
                  toStockPointId: item.toStockPointId
                }))
              };

              const createUrl = updateStandardLocation 
                ? '/fortnox/stocktransfer/?update_standard_location=true'
                : '/fortnox/stocktransfer/';
              const response = await api.post(createUrl, transferData);
              
              if (response.status === 200 || response.status === 201) {
                // Hämta document ID från response
                const documentId = response.data?.id;
                
                if (documentId) {
                  try {
                    // Försök att release/klarmarkera dokumentet direkt
                    console.log(`Attempting to release document ${documentId}`);
                    await api.put(`/fortnox/stocktransfer/${documentId}/release/`);
                    console.log(`Document ${documentId} released successfully`);
                    
                    presentAlert({
                      header: 'Flytt genomförd',
                      message: 'Lagerflyttningen har registrerats och klarmarkerats',
                      buttons: ['OK']
                    });
                  } catch (releaseError: any) {
                    console.error('Failed to release document:', releaseError);
                    // Dokumentet skapades men kunde inte klarmarkeras
                    presentAlert({
                      header: 'Flytt genomförd',
                      message: `Lagerflyttningen har registrerats men kunde inte klarmarkeras automatiskt (Dokument #${documentId}). Du kan behöva klarmarkera den manuellt.`,
                      buttons: ['OK']
                    });
                  }
                } else {
                  presentAlert({
                    header: 'Flytt genomförd',
                    message: 'Lagerflyttningen har registrerats',
                    buttons: ['OK']
                  });
                }
                
                // Återställ allt
                resetForm();
                setTransferItems([]);
              }
            } catch (error: any) {
              console.error('Failed to transfer stock:', error);
              console.error('Error response:', error.response);
              
              let errorMessage = 'Kunde inte genomföra lagerflyttningen.';
              
              if (error.response?.status === 401) {
                errorMessage = 'Du är inte behörig att utföra denna åtgärd. Logga in igen eller kontakta administratör.';
              } else if (error.response?.status === 403) {
                errorMessage = 'Du saknar behörighet för lagerflytt. Kontakta administratör.';
              } else if (error.response?.data) {
                // Försök extrahera felmeddelande från olika format
                if (typeof error.response.data === 'string') {
                  errorMessage = error.response.data;
                } else if (error.response.data.message) {
                  errorMessage = error.response.data.message;
                } else if (error.response.data.detail) {
                  errorMessage = error.response.data.detail;
                } else if (error.response.data.error) {
                  errorMessage = error.response.data.error;
                } else {
                  errorMessage = JSON.stringify(error.response.data);
                }
              }
              
              presentAlert({
                header: `Fel (${error.response?.status || 'Okänd'})`,
                message: errorMessage,
                buttons: ['OK']
              });
            } finally {
              setIsLoading(false);
            }
          }
        }
      ]
    });
  };

  const resetForm = () => {
    setArticleNumber('');
    setExactArticleNumber('');
    setStockSummary([]);
    setFromStockPoint('');
    setFromStockLocation('');
    setToStockPoint('');
    setToStockLocation('');
    setQuantity(1);
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {scannerActive ? (
              <IonButton onClick={handleStopScanner} color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon slot="icon-only" icon={closeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
            ) : (
              <IonBackButton defaultHref="/Home" />
            )}
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {scannerActive ? 'Skanna streckkod' : 'Lagerflytt'}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <div 
          id="data-capture-view" 
          style={{ position: 'fixed', inset: 0, zIndex: 20, pointerEvents: 'none', display: scannerActive ? 'block' : 'none' }} 
        />
        
        {/* Loading overlay när skannern startar */}
        {scannerActive && scannerLoading && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 21,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            pointerEvents: 'none'
          }}>
            <div style={{ textAlign: 'center', color: 'white' }}>
              <IonSpinner name="crescent" style={{ width: '48px', height: '48px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              <div style={{ marginTop: '16px', fontSize: '16px' }}>Startar kamera...</div>
            </div>
          </div>
        )}

        <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Sök artikel</IonCardTitle>
          </IonCardHeader>
          <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Artikelnummer</IonLabel>
              <IonInput
                value={articleNumber}
                placeholder="Ange artikelnummer"
                onIonChange={e => setArticleNumber(e.detail.value!)}
                clearInput
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <IonButton 
                expand="block" 
                onClick={scannerActive ? handleStopScanner : handleActivateScanner} 
                disabled={scannerLoading || isLoading}
                color={scannerActive ? 'danger' : 'primary'}
                placeholder={undefined} 
                onPointerEnterCapture={undefined} 
                onPointerLeaveCapture={undefined}
              >
                <IonIcon slot="start" icon={barcodeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                {scannerLoading ? 'Startar...' : scannerActive ? 'Stoppa skanner' : 'Skanna'}
              </IonButton>
              <IonButton expand="block" onClick={handleManualSearch} disabled={isLoading || scannerActive} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon slot="start" icon={searchOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                Sök
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <IonSpinner placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            <IonText placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <p>Söker artikel...</p>
            </IonText>
          </div>
        )}

        {stockSummary.length > 0 && (
          <>
            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Nuvarande lagersaldo</IonCardTitle>
              </IonCardHeader>
              <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  {stockSummary.map((stock, index) => (
                    <div key={index}>
                      <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <h2>{stock.stockPointCode} - {stock.stockPointName}</h2>
                          <p>Totalt: {stock.totalQuantity} st | Tillgängligt: {stock.availableQuantity} st</p>
                        </IonLabel>
                      </IonItem>
                      {stock.stockLocationDetails.map((location, locIndex) => (
                        location.stockLocationCode && (
                          <IonItem key={`${index}-${locIndex}`} style={{ paddingLeft: '20px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                              <h3>Plats: {location.stockLocationCode}</h3>
                              <p>Totalt: {location.totalQuantity} st | Tillgängligt: {location.availableQuantity} st</p>
                            </IonLabel>
                          </IonItem>
                        )
                      ))}
                    </div>
                  ))}
                </IonList>
              </IonCardContent>
            </IonCard>

            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonIcon icon={swapHorizontalOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /> Flytt
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonListHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Från</IonLabel>
                </IonListHeader>
                
                <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Lagerplats (StockPoint)</IonLabel>
                  <IonSelect
                    value={fromStockPoint}
                    placeholder="Välj lagerplats med lager"
                    onIonChange={e => handleStockPointChange(e.detail.value, true)}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    {availableStockPoints.map(sp => {
                      const stock = stockSummary.find(s => s.stockPointCode === sp.code);
                      return (
                        <IonSelectOption key={sp.code} value={sp.code} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          {sp.code} - {sp.name} ({stock?.availableQuantity || 0} st)
                        </IonSelectOption>
                      );
                    })}
                  </IonSelect>
                </IonItem>

                <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Lagerlokal (StockLocation)</IonLabel>
                  <IonSelect
                    value={fromStockLocation}
                    placeholder="Välj lagerlokal med lager"
                    onIonChange={e => handleFromLocationChange(e.detail.value)}
                    disabled={!fromStockPoint}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    {availableFromLocations.map((loc, index) => {
                      const stockForPoint = stockSummary.find(s => s.stockPointCode === fromStockPoint);
                      const detail = stockForPoint?.stockLocationDetails.find(d => d.stockLocationCode === loc.code || (d.stockLocationCode === null && loc.code === ''));
                      return (
                        <IonSelectOption key={loc.code || `no-location-${index}`} value={loc.code} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          {loc.code || loc.name} {loc.name && loc.code ? `- ${loc.name}` : ''} ({detail?.availableQuantity || 0} st)
                        </IonSelectOption>
                      );
                    })}
                  </IonSelect>
                </IonItem>

                <IonListHeader style={{ marginTop: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Till</IonLabel>
                </IonListHeader>

                <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Lagerlokal (StockPoint)</IonLabel>
                  <IonSelect
                    value={toStockPoint}
                    placeholder="Välj lagerlokal"
                    onIonChange={e => handleStockPointChange(e.detail.value, false)}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    {stockPoints.map(sp => (
                      <IonSelectOption key={sp.code} value={sp.code} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        {sp.code} - {sp.name}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>

                <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Lagerplats (StockLocation)</IonLabel>
                  <IonInput
                    placeholder="Sök och välj lagerplats..."
                    onIonChange={(e: any) => handleToStockLocationSearch(e.detail.value || '')}
                    onIonFocus={() => {
                      if (!justSelectedLocation.current && toStockLocationSearch && filteredToStockLocations.length > 0) {
                        setShowToStockLocationSuggestions(true);
                      }
                    }}
                    disabled={!toStockPoint}
                    debounce={100}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </IonItem>
                {showToStockLocationSuggestions && filteredToStockLocations.length > 0 && (
                  <IonCard style={{ margin: '0 16px', maxHeight: '200px', overflow: 'auto' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      {filteredToStockLocations.map(loc => (
                        <IonItem
                          key={loc.code}
                          button
                          onClick={() => handleSelectToStockLocation(loc)}
                          placeholder={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <h3>{loc.code}</h3>
                            {loc.name && <p>{loc.name}</p>}
                          </IonLabel>
                        </IonItem>
                      ))}
                    </IonList>
                  </IonCard>
                )}

                <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    Antal (max {getMaxAvailableQuantity()} st)
                  </IonLabel>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px', marginTop: '8px' }}>
                    <IonButton
                      fill="outline"
                      size="default"
                      onClick={handleDecreaseQuantity}
                      disabled={quantity <= 1}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonIcon slot="icon-only" icon={removeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </IonButton>
                    <IonInput
                      type="number"
                      value={quantity}
                      placeholder="Antal"
                      onIonChange={e => handleQuantityChange(e.detail.value!)}
                      min={1}
                      max={getMaxAvailableQuantity()}
                      style={{ textAlign: 'center', flex: 1 }}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    <IonButton
                      fill="outline"
                      size="default"
                      onClick={handleIncreaseQuantity}
                      disabled={quantity >= getMaxAvailableQuantity()}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonIcon slot="icon-only" icon={addOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </IonButton>
                  </div>
                </IonItem>

                <IonButton
                  expand="block"
                  style={{ marginTop: '16px' }}
                  onClick={handleAddToList}
                  color="success"
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  <IonIcon slot="start" icon={swapHorizontalOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  Lägg till i lista
                </IonButton>
              </IonCardContent>
            </IonCard>
          </>
        )}

        {transferItems.length > 0 && (
          <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                Artiklar att flytta ({transferItems.length})
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {transferItems.map((item, index) => (
                  <IonItem key={index} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <h3>{item.articleNumber}</h3>
                      <p>Antal: {item.quantity} st</p>
                      <p>
                        Från: {item.fromStockPointCode}{item.fromStockLocationCode ? ` / ${item.fromStockLocationCode}` : ''}
                      </p>
                      <p>
                        Till: {item.toStockPointCode}{item.toStockLocationCode ? ` / ${item.toStockLocationCode}` : ''}
                      </p>
                    </IonLabel>
                    <IonButton 
                      slot="end" 
                      fill="clear" 
                      color="danger" 
                      onClick={() => handleRemoveItem(index)}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonIcon slot="icon-only" icon={closeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </IonButton>
                  </IonItem>
                ))}
              </IonList>

              <IonItem lines="none" style={{ marginTop: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <h3>Uppdatera standardplats</h3>
                  <p>Sätt artiklarnas standardplats till den nya platsen</p>
                </IonLabel>
                <IonCheckbox
                  slot="start"
                  checked={updateStandardLocation}
                  onIonChange={e => setUpdateStandardLocation(e.detail.checked)}
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </IonItem>

              <IonButton
                expand="block"
                style={{ marginTop: '16px' }}
                onClick={handleTransfer}
                color="primary"
                disabled={isLoading}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                {isLoading ? (
                  <>
                    <IonSpinner name="crescent" style={{ marginRight: '8px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    Genomför flytt...
                  </>
                ) : (
                  <>
                    <IonIcon slot="start" icon={swapHorizontalOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    Genomför flytt
                  </>
                )}
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}
      </IonContent>
    </IonPage>
  );
};

export default WarehouseTransfer;
